import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus, Search } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UploadZone } from "@/components/UploadZone";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function MappingsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [total, setTotal] = useState(0);
  const [form, setForm] = useState({ sku_id: "", warehouse_id: "", master_sku: "", platform: "", product_name: "" });

  const load = useCallback(async () => {
    const [{ data }, s] = await Promise.all([api.get("/mappings", { params: { search: search || undefined } }), api.get("/stats")]);
    setRows(data);
    setTotal(s.data.mappings);
  }, [search]);
  useEffect(() => { load(); }, [load]);

  const upload = async (files, replace) => {
    const fd = new FormData();
    fd.append("file", files[0]);
    fd.append("replace", replace ? "true" : "false");
    setBusy(true);
    try {
      const { data } = await api.post("/mappings/upload", fd);
      toast.success(`${data.imported} imported · ${data.total} total · ${data.rematched_labels} label(s) re-matched`);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/mappings", form);
      toast.success("Mapping saved");
      setForm({ sku_id: "", warehouse_id: "", master_sku: "", platform: "", product_name: "" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (id) => { await api.delete(`/mappings/${id}`); load(); };
  const clearAll = async () => {
    if (!window.confirm("Delete ALL mappings?")) return;
    await api.delete("/mappings");
    toast.success("All mappings cleared");
    load();
  };

  return (
    <div className="space-y-5" data-testid="mappings-page">
      <div>
        <div className="font-mono text-[11px] uppercase tracking-widest text-emerald-400">{total} mappings{rows.length < total ? ` · showing ${rows.length}` : ""}</div>
        <h1 className="font-heading text-3xl font-extrabold uppercase tracking-tight sm:text-4xl lg:text-5xl">SKU → Warehouse mapping</h1>
      </div>

      <div className="grid gap-4 sm:gap-6 lg:grid-cols-[1fr_1fr]">
        <UploadZone testId="upload-mapping-dropzone" title="Upload mapping file" hint="CSV / Excel · columns: platform_sku (or SKU ID), warehouse_code, master_sku, platform, product_name. Existing SKUs are updated." accept=".csv,.xlsx,.xls" onFiles={(f) => upload(f, false)} busy={busy} />
        <form onSubmit={add} className="space-y-3 rounded-md border border-slate-800 bg-[#111827] p-5" data-testid="add-mapping-form">
          <div className="font-heading text-lg font-bold uppercase tracking-tight">Add single mapping</div>
          <div className="grid gap-3 sm:grid-cols-3">
            <Input data-testid="mapping-sku-input" placeholder="Platform SKU (on label)" required value={form.sku_id} onChange={(e) => setForm({ ...form, sku_id: e.target.value })} className="font-mono border-slate-700 bg-slate-950" />
            <Input data-testid="mapping-warehouse-input" placeholder="Warehouse ID" required value={form.warehouse_id} onChange={(e) => setForm({ ...form, warehouse_id: e.target.value })} className="font-mono border-slate-700 bg-slate-950" />
            <Input data-testid="mapping-master-input" placeholder="Master SKU (optional)" value={form.master_sku} onChange={(e) => setForm({ ...form, master_sku: e.target.value })} className="font-mono border-slate-700 bg-slate-950" />
            <Input data-testid="mapping-platform-input" placeholder="Platform (Amazon/Flipkart/…)" value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })} className="border-slate-700 bg-slate-950" />
            <Input data-testid="mapping-product-input" placeholder="Product name" value={form.product_name} onChange={(e) => setForm({ ...form, product_name: e.target.value })} className="border-slate-700 bg-slate-950" />
          </div>
          <div className="flex justify-between">
            {isAdmin ? <Button type="button" variant="ghost" className="text-red-300 hover:bg-red-950/40" onClick={clearAll} data-testid="clear-mappings-button"><Trash2 className="mr-1 h-4 w-4" /> Clear all</Button> : <span />}
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="add-mapping-button"><Plus className="mr-1 h-4 w-4" /> Add</Button>
          </div>
        </form>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-500" />
        <Input data-testid="mappings-search-input" placeholder="Search SKU / warehouse / product" value={search} onChange={(e) => setSearch(e.target.value)} className="border-slate-700 bg-slate-950 pl-8 text-xs" />
      </div>

      <div className="overflow-x-auto rounded-md border border-slate-800 bg-[#111827]">
        <Table data-testid="mappings-table">
          <TableHeader><TableRow className="border-slate-800 hover:bg-transparent">
            <TableHead className="font-mono text-[11px] uppercase tracking-widest">Warehouse</TableHead>
            <TableHead className="font-mono text-[11px] uppercase tracking-widest">Master SKU</TableHead>
            <TableHead className="font-mono text-[11px] uppercase tracking-widest">Platform SKU</TableHead>
            <TableHead className="font-mono text-[11px] uppercase tracking-widest">Platform</TableHead>
            <TableHead className="font-mono text-[11px] uppercase tracking-widest">Product</TableHead>
            <TableHead className="w-12" />
          </TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={6} className="py-10 text-center text-slate-500" data-testid="mappings-empty">No mappings yet. Upload a CSV/Excel to get started.</TableCell></TableRow>}
            {rows.map((r) => (
              <TableRow key={r.id} className="row-hover border-slate-800/70 hover:bg-slate-800/40" data-testid={`mapping-row-${r.id}`}>
                <TableCell className="font-mono text-sm font-semibold text-blue-300">{r.warehouse_id}</TableCell>
                <TableCell className="font-mono text-sm">{r.master_sku || r.sku_id}</TableCell>
                <TableCell className="font-mono text-xs text-slate-300">{r.sku_id}</TableCell>
                <TableCell className="font-mono text-xs uppercase text-slate-400">{r.platform || "any"}</TableCell>
                <TableCell className="text-sm text-slate-300">{r.product_name || "—"}</TableCell>
                <TableCell>{isAdmin && <Button size="icon" variant="ghost" className="h-8 w-8 text-red-300" onClick={() => remove(r.id)} data-testid={`mapping-delete-${r.id}`}><Trash2 className="h-4 w-4" /></Button>}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
